import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, Plus, Trash2, Loader2, CheckCircle, AlertCircle, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { myVehiclesAPI, vehiclesAPI, vehicleAlertsAPI } from '../services/api';
import { vehicleStore, useSelectedVehicle } from '../services/vehicleStore';
import { garageStore } from '../services/garageStore';
import { useT } from '../services/i18n';
import VehicleBrandLogo from './VehicleBrandLogo';
import GarageAlertSuggestion from './GarageAlertSuggestion';
import GarageCompare from './GarageCompare';
import { GarageBudgetCard } from './GarageBudgetCard';
import { GarageAdditivesCard } from './GarageAdditivesCard';

const Select = ({ testId, value, onChange, disabled, placeholder, options }) => (
  <select data-testid={testId} value={value} onChange={onChange} disabled={disabled}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white disabled:bg-gray-100 disabled:text-gray-400">
    <option value="">{placeholder}</option>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

const MyVehiclesSection = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const selected = useSelectedVehicle();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [engines, setEngines] = useState([]);
  const [form, setForm] = useState({ brand: '', model: '', year: '', engine_name: '', plate: '', label: '' });
  const [withAlert, setWithAlert] = useState(true);

  const load = () => myVehiclesAPI.list().then(v => { setVehicles(v); garageStore.invalidate(); }).catch(() => setVehicles([])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  useEffect(() => { if (adding && brands.length === 0) vehiclesAPI.getBrands().then(setBrands).catch(() => {}); }, [adding, brands.length]);
  useEffect(() => { setModels([]); if (form.brand) vehiclesAPI.getModels(form.brand).then(setModels).catch(() => {}); }, [form.brand]);
  useEffect(() => { setYears([]); if (form.brand && form.model) vehiclesAPI.getYears(form.brand, form.model).then(setYears).catch(() => {}); }, [form.brand, form.model]);
  useEffect(() => { setEngines([]); if (form.brand && form.model && form.year) vehiclesAPI.getEngines(form.brand, form.model, form.year).then(setEngines).catch(() => {}); }, [form.brand, form.model, form.year]);

  const openForm = () => { setForm({ brand: '', model: '', year: '', engine_name: '', plate: '', label: '' }); setMessage(null); setAdding(true); };

  const save = async () => {
    setMessage(null);
    if (!form.brand || !form.model || !form.year) { setMessage({ type: 'error', text: t('garage.selectRequired') }); return; }
    setSaving(true);
    try {
      const created = await myVehiclesAPI.add({ brand: form.brand, model: form.model, year: Number(form.year), engine_name: form.engine_name || null, plate: form.plate || null, label: form.label || null });
      let alertNote = '';
      if (withAlert && created?.id) {
        try { const r = await vehicleAlertsAPI.fromGarage([created.id]); alertNote = r.count ? ` ${t('gar.alertEnabledNote')}` : ''; } catch { alertNote = ''; }
      }
      await load();
      setAdding(false);
      setMessage({ type: 'success', text: (form.plate ? t('garage.addedPlate') : t('garage.added')) + alertNote });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || t('garage.addError') });
    } finally { setSaving(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(t('garage.confirmDelete', { name: `${v.brand} ${v.model}${v.plate ? ` (${v.plate})` : ''}` }))) return;
    await myVehiclesAPI.remove(v.id);
    await load();
    setMessage({ type: 'success', text: t('garage.deleted') });
  };

  const use = (v) => {
    vehicleStore.set({ brand: v.brand, model: v.model, year: v.year, engine: v.engine, plate: v.plate });
    navigate('/pieces');
  };

  const isSelected = (v) => selected && selected.brand === v.brand && selected.model === v.model && selected.year === v.year && selected.engine?.name === v.engine?.name;

  return (
    <section className="bg-white rounded-xl shadow-sm p-6 lg:col-span-2" data-testid="my-vehicles-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Car className="text-orange-500" size={20} /> {t('garage.title')}</h2>
        {!adding && (
          <Button data-testid="my-vehicles-add-button" size="sm" onClick={openForm} disabled={vehicles.length >= 10} className="bg-orange-500 hover:bg-orange-600">
            <Plus size={16} className="mr-1" /> {t('garage.add')}
          </Button>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-4">{t('garage.intro')}</p>

      {message && (
        <p data-testid="my-vehicles-message" className={`mb-4 text-sm flex items-center gap-2 ${message.type === 'error' ? 'text-red-600' : 'text-green-700'}`}>
          {message.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />} {message.text}
        </p>
      )}

      {adding && (
        <div className="border border-orange-200 bg-orange-50/40 rounded-lg p-4 mb-4" data-testid="my-vehicle-form">
          <div className="flex items-center justify-between mb-3">
            <p className="font-medium text-gray-900">{t('garage.newVehicle')}</p>
            <button onClick={() => setAdding(false)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Select testId="my-vehicle-brand" value={form.brand} onChange={(e) => setForm(f => ({ ...f, brand: e.target.value, model: '', year: '', engine_name: '' }))} placeholder={t('garage.brand')} options={brands.map(b => ({ value: b, label: b }))} />
            <Select testId="my-vehicle-model" value={form.model} disabled={!form.brand} onChange={(e) => setForm(f => ({ ...f, model: e.target.value, year: '', engine_name: '' }))} placeholder={t('garage.model')} options={models.map(m => ({ value: m, label: m }))} />
            <Select testId="my-vehicle-year" value={form.year} disabled={!form.model} onChange={(e) => setForm(f => ({ ...f, year: e.target.value, engine_name: '' }))} placeholder={t('garage.year')} options={years.map(y => ({ value: String(y), label: String(y) }))} />
            <Select testId="my-vehicle-engine" value={form.engine_name} disabled={!form.year} onChange={(e) => setForm(f => ({ ...f, engine_name: e.target.value }))} placeholder={t('garage.engine')} options={engines.map(en => ({ value: en.name, label: `${en.name} (${en.fuel}, ${en.power} ${t('garage.hp')})` }))} />
            <div className="sm:col-span-2">
              <Input data-testid="my-vehicle-plate" value={form.plate} onChange={(e) => setForm(f => ({ ...f, plate: e.target.value.toUpperCase() }))} placeholder={t('garage.platePh')} maxLength={12} className="font-mono" />
            </div>
            <div className="sm:col-span-2">
              <Input data-testid="my-vehicle-label" value={form.label} onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))} placeholder={t('garage.labelPh')} maxLength={40} />
            </div>
          </div>
          <label className="mt-3 flex items-start gap-2 text-sm text-gray-700 cursor-pointer" data-testid="my-vehicle-alert-optin-label">
            <input type="checkbox" checked={withAlert} onChange={(e) => setWithAlert(e.target.checked)} data-testid="my-vehicle-alert-optin" className="mt-0.5 accent-orange-500" />
            <span>{t('gar.alertOptIn')} <span className="text-xs text-gray-500">{t('gar.alertOptInHint')}</span></span>
          </label>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={() => setAdding(false)}>{t('garage.cancel')}</Button>
            <Button data-testid="my-vehicle-submit" size="sm" onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-700">
              {saving && <Loader2 size={14} className="mr-1 animate-spin" />} {t('garage.save')}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="animate-spin text-orange-500" /></div>
      ) : vehicles.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6 border border-dashed rounded-lg" data-testid="my-vehicles-empty">{t('garage.empty')}</p>
      ) : (
        <>
        <GarageCompare vehicles={vehicles} />
        <GarageBudgetCard vehiclesCount={vehicles.length} />
        <GarageAdditivesCard vehiclesCount={vehicles.length} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="my-vehicles-list">
          {vehicles.map(v => (
            <div key={v.id} data-testid={`my-vehicle-${v.id}`} className={`border rounded-lg p-4 flex gap-3 ${isSelected(v) ? 'border-orange-400 bg-orange-50/40' : 'border-gray-200'}`}>
              <VehicleBrandLogo brand={v.brand} model={v.model} size={56} preferModel zoom testId={`my-vehicle-logo-${v.id}`} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{v.label || `${v.brand} ${v.model}`}</p>
                <p className="text-sm text-gray-600">{v.brand} {v.model} · {v.year}{v.engine ? ` · ${v.engine.name}` : ''}</p>
                {v.plate && (
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <p className="text-xs font-mono inline-block bg-gray-900 text-white px-2 py-0.5 rounded" data-testid={`my-vehicle-plate-${v.id}`}>{v.plate}</p>
                    {v.plate_status && (
                      <span data-testid={`my-vehicle-plate-status-${v.id}`} className={`text-[11px] px-2 py-0.5 rounded-full ${v.plate_status === 'validated' ? 'bg-green-100 text-green-700' : v.plate_status === 'refused' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {v.plate_status === 'validated' ? t('garage.plateValidated') : v.plate_status === 'refused' ? t('garage.plateRefused') : t('garage.platePending')}
                      </span>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                  <button data-testid={`my-vehicle-use-${v.id}`} onClick={() => use(v)} className="text-xs text-orange-600 hover:underline font-medium">
                    {isSelected(v) ? t('garage.useSelected') : t('garage.use')}
                  </button>
                  <button data-testid={`my-vehicle-delete-${v.id}`} onClick={() => remove(v)} className="text-xs text-red-600 hover:underline flex items-center gap-1"><Trash2 size={12} /> {t('garage.delete')}</button>
                  <GarageAlertSuggestion vehicleId={v.id} compact />
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
      )}
    </section>
  );
};

export default MyVehiclesSection;
